import { toPaidFlag } from '../models/invoice-payment-status';
import {
    Filter,
    FiscalDevice,
    FiscalDeviceDTO,
    ManagerAssignableUser,
    TableData,
    TurnoverFilter,
    User,
} from '../models/table-models';
import { TableRequestParams, transformNoPagerResponse, transformTableResponse } from './data-hooks';
import interceptor from '../restServiceInterceptor';
import {
    DefaultView,
    LoggedUser,
    MonthlyReport,
    TotalTurnoverReport,
    toUserFilter,
    TurnoverDTO,
    UserFilter,
    UserFull,
} from '../models/user-models';
import { formatTurnover } from './warehouses-hooks';
import { Pagination } from './pagination-hooks';
import { InvoicelessDocumentView } from '../models/dashboard-models';
import { DistributorDevice, DistributorLocationPoint, RouteBranchMarker } from '../models/distributor-location-models';
import { Role } from './Enums/Roles';

export const useGetUsers = (props?: TableRequestParams, roles?: string): Promise<TableData<User[]>> => {
    let userFilter: UserFilter = {};
    if (props?.filter) userFilter = toUserFilter(props.filter);
    if (roles) userFilter.roles = roles;
    const page = props && props['pager.page'];
    const itemsPerPage = props && props['pager.itemsPerPage'];
    //todo: if roles is used, set itemsPerPage to a big number so that selects are not missing users

    return interceptor.get('/user-service/api/admin/getAll', {
        params: {
            page,
            itemsPerPage,
            ...userFilter,
        },
        transformResponse: transformTableResponse,
    });
};
export const useGetUsersInGroups = (mainGroup: string, subGroups: string[]): Promise<User[]> => {
    const groups = subGroups.map((value) => mainGroup + '-' + value).join(',');
    return interceptor.get('/user-service/api/admin/groups/getUsersForGroups', {
        params: {
            groupNames: groups,
        },
    });
};
export const useGetDeletedUsers = (props?: TableRequestParams): Promise<TableData<User[]>> => {
    let userFilter: UserFilter = {};
    if (props?.filter) userFilter = toUserFilter(props.filter);
    const page = props && props['pager.page'];
    const itemsPerPage = props && props['pager.itemsPerPage'];

    return interceptor.get('/user-service/api/admin/getAllDeletedUsers', {
        params: {
            page,
            itemsPerPage,
            ...userFilter,
        },
        transformResponse: transformTableResponse,
    });
};
export const useRestoreDeletedUser = ({ id }: User) => {
    return interceptor.post(
        '/user-service/api/admin/restoreUser',
        {},
        {
            params: {
                deletedUserId: id,
            },
        }
    );
};

export const useDeleteUser = (fields: User) => {
    return interceptor.post(`/user-service/api/admin/deleteUser?id=${fields.id}`);
};

export const fetchDistributorLocationHistory = (userId: number, date: string): Promise<DistributorLocationPoint[]> => {
    return interceptor.get('/work-service/distributor-locations', {
        params: {
            userId,
            date,
        },
    });
};

export const fetchDistributorRouteBranchMarkers = (userId: number, date: string): Promise<RouteBranchMarker[]> => {
    return interceptor.get('/work-service/distributor-locations/branch-markers', {
        params: {
            userId,
            date,
        },
    });
};

// Кой телефон е ползвал всеки от подадените потребители последно. Потребители без нито една
// локация просто липсват в отговора.
export const fetchDistributorDevices = (userIds: number[]): Promise<DistributorDevice[]> => {
    return interceptor.get('/work-service/distributor-locations/devices', {
        params: {
            userIds: userIds.join(','),
        },
    });
};

// Всички апарати, от които един потребител е пращал локации, най-новият пръв. Дърпа се само за
// отворения профил: групирането върху цяла страница потребители би минало през всичките им редове.
export const fetchDistributorDevicesForUser = (userId: number): Promise<DistributorDevice[]> => {
    return interceptor.get(`/work-service/distributor-locations/devices/${userId}`);
};

export const useUpdateUser = (fields: User) => {
    const {
        id,
        username,
        password,
        group,
        name,
        phone,
        email,
        comment,
        roles,
        town,
        invoiceStartNumber,
        invoiceEndNumber,
        receiptStartNumber,
        receiptEndNumber,
        maintenancedays,
        assignedUserIds,
        mobileDeviceId,
    } = fields;
    return interceptor.post('user-service/api/admin/updateUser', {
        id,
        username,
        password,
        group,
        defaultView: {
            zoom: '0',
            longitude: '27.910543',
            latitude: '43.204666',
        },
        name,
        phone,
        email,
        comment,
        roles,
        town,
        invoiceStartNumber,
        invoiceEndNumber,
        receiptStartNumber,
        receiptEndNumber,
        maintenancedays,
        assignedUserIds: roles.includes(Role.MANAGER) ? assignedUserIds ?? [] : [],
        // Формата винаги праща полето: празен низ значи "разкачи телефона". Ако някой ден друг
        // извикващ го пропусне, сървърът пази записания, вместо да го изтрие.
        mobileDeviceId,
    });
};
export const useAddUser = (fields: User) => {
    const {
        username,
        password,
        group,
        name,
        phone,
        email,
        comment,
        roles,
        town,
        invoiceStartNumber,
        invoiceEndNumber,
        receiptStartNumber,
        receiptEndNumber,
        assignedUserIds,
        mobileDeviceId,
    } = fields;
    return interceptor.post('user-service/api/admin/createUser', {
        username,
        password,
        group,
        defaultView: {
            zoom: '0',
            longitude: '27.910543',
            latitude: '43.204666',
        },
        name,
        phone,
        email,
        comment,
        roles,
        town,
        invoiceStartNumber,
        invoiceEndNumber,
        receiptStartNumber,
        receiptEndNumber,
        assignedUserIds: roles.includes(Role.MANAGER) ? assignedUserIds ?? [] : [],
        mobileDeviceId,
    });
};

export const useGetManagerEligibleAssignees = (): Promise<ManagerAssignableUser[]> => {
    return interceptor.get('/user-service/api/admin/manager/eligibleAssignees');
};

export const getUserByUsername = (username: string): Promise<LoggedUser> => {
    return interceptor.get('user-service/api/user/services/getByUsername', {
        params: {
            username,
        },
        transformResponse: transformNoPagerResponse,
    });
};
export const getLoggedUser = (): Promise<LoggedUser> => {
    return interceptor.get('user-service/api/user/services/getLoggedUser', {
        transformResponse: transformNoPagerResponse,
    });
};
export const getUserById = (userId: number): Promise<UserFull> => {
    return interceptor.get('user-service/api/user/services/getById', {
        params: {
            userId,
        },
        transformResponse: transformNoPagerResponse,
    });
};

export const useGetAllRoles = (): Promise<string[]> => {
    return interceptor.get('user-service/api/admin/roles/getAllRoleNames');
};

export const useGetDefaultView = (): Promise<DefaultView> => {
    return interceptor.get('/user-service/api/admin/getDefaultView');
};

export const useGetAllTurnovers = (pagination: Pagination, filter: TurnoverFilter): Promise<TurnoverDTO> => {
    return interceptor.get('/client-service/api/user/turnover', {
        params: {
            ...turnoverParams(filter),
            pageSize: pagination.pageSize,
            pageIndex: pagination.currentPage,
        },
        transformResponse: formatTurnover,
    });
};

export const getTurnoverExport = (filter: TurnoverFilter): Promise<import('../models/user-models').TurnoverExportDTO> =>
    interceptor.get('/client-service/api/user/turnover/export', {params: turnoverParams(filter)});

const turnoverParams = (filter: TurnoverFilter) => {
    const search = filter.search;
    const getChars = filter.getChars ?? true;
    const beforeDate = filter.before;
    const afterDate = filter.after;
    const userIds: string | null = filter.userIds && filter.userIds?.length > 0 ? filter.userIds.join(',') : null;
    const branchIds: string | null =
        filter.branchIds && filter.branchIds?.length > 0 ? filter.branchIds.join(',') : null;
    const devicePassportIds: string | null =
        filter.passportIds && filter.passportIds.length > 0 ? filter.passportIds.join(',') : null;
    const oilIds: string | null = filter.oilIds && filter.oilIds.length > 0 ? filter.oilIds.join(',') : null;
    const paymentMethod =
        filter.paymentMethod === 'all' || toPaidFlag(filter.paymentMethod) !== null
            ? null
            : filter.paymentMethod ?? null;
    const type = filter.type || null;
    const saleType = filter.saleType && filter.saleType !== '' ? filter.saleType : null;
    return {
            search,
            userIds,
            branchIds,
            beforeDate,
            afterDate,
            devicePassportIds,
            oilIds,
            paymentMethod,
            type,
            saleType,
            paid: filter.paid || null,
            getChars,
    };
};
export const useGetAllUnInvoicedDocuments = (
    pagination: Pagination,
    filter: TurnoverFilter
): Promise<TableData<InvoicelessDocumentView[]>> => {
    return interceptor.get('/client-service/docs/getAllDocumentsWithoutInvoice', {
        params: {
            itemsPerPage: pagination.pageSize,
            page: pagination.currentPage,
            // documentFailType: null
        },
        transformResponse: transformTableResponse,
    });
};

export const useGetAllDocuments = (
    pagination: Pagination,
    filter: TurnoverFilter
): Promise<TableData<InvoicelessDocumentView[]>> => {
    const beforeDate = filter.before;
    const afterDate = filter.after;
    const userIds: string | null = filter.userIds && filter.userIds?.length > 0 ? filter.userIds.join(',') : null;
    const branchIds: string | null =
        filter.branchIds && filter.branchIds?.length > 0 ? filter.branchIds.join(',') : null;
    const paymentMethod =
        filter.paymentMethod === 'all' || toPaidFlag(filter.paymentMethod) !== null
            ? null
            : filter.paymentMethod ?? null;
    const type = filter.type ?? null;
    const saleType = filter.saleType ?? null;
    return interceptor.get('/client-service/docs/getAllDocuments', {
        params: {
            pageSize: pagination.pageSize,
            pageIndex: pagination.currentPage,
            userIds,
            branchIds,
            beforeDate,
            afterDate,
            paymentMethod,
            type,
            saleType,
        },
        transformResponse: transformTableResponse,
    });
};

export const useGetTotalTurnovers = (
    ids: string[],
    { before: beforeDate, after: afterDate }: Filter
): Promise<TotalTurnoverReport> => {
    const userIds: string | null = ids?.length > 0 ? ids.join(',') : null;
    return interceptor.get('/client-service/api/user/turnover/total', { params: { userIds, beforeDate, afterDate } });
};

export const useGetMonthlyReport = (filter: TurnoverFilter): Promise<MonthlyReport[]> => {
    const createdBefore = filter.before;
    const createdAfter = filter.after;
    return interceptor.get('/client-service/api/user/turnover/report', {
        params: {
            createdBefore,
            createdAfter,
        },
    });
};

export const useGetFiscalDevice = (id: string): Promise<FiscalDeviceDTO> => {
    return interceptor.get(`/user-service/api/admin/fiscalDevice/get?id=${id}`, {
        transformResponse: transformNoPagerResponse,
    });
};

export const useGetAllFiscalDevices = (props?: TableRequestParams): Promise<TableData<FiscalDeviceDTO[]>> => {
    const page = props && props['pager.page'];
    const itemsPerPage = props && props['pager.itemsPerPage'];

    return interceptor.get(`/user-service/api/admin/fiscalDevice/get`, {
        params: {
            page,
            itemsPerPage,
            searchBy: props?.filter?.search ?? '',
            sortBy: props?.filter?.sortBy || undefined,
            sortDirection: props?.filter?.sortDirection || undefined,
        },
        transformResponse: transformTableResponse,
    });
};

export const useGetFiscalDeviceGetByUserId = (id: string): Promise<FiscalDeviceDTO> => {
    return interceptor.get(`/user-service/api/admin/fiscalDevice/getByUserId?id=${id}`, {
        transformResponse: transformNoPagerResponse,
    });
};

export const useAddOrEditFiscalDevice = (fields: FiscalDevice): Promise<FiscalDevice> => {
    return interceptor.post('/user-service/api/admin/fiscalDevice/createOrUpdate', {
        ...fields,
    });
};

export const changeFiscalDeviceUser = (deviceId: number, userId: number | undefined): Promise<void> => {
    return interceptor.post('/user-service/api/admin/fiscalDevice/changeDeviceForUser', {
        deviceId,
        userId,
    });
};

export const useDeleteFiscalDevice = (id?: number): Promise<void> => {
    return interceptor.delete(`/user-service/api/admin/fiscalDevice/delete?id=${id}`, {});
};
